import {ErrorBoundary, useErrorBoundary} from "react-error-boundary";
import {useEffect, useRef, useState} from "react";
import {captureMessage} from "../lib/utils";
import {useConversationContext} from "../contexts/ConversationContext";
import {cleanTurn} from "../llm/LLMClient";
import {toJS} from "mobx";
import {DEV} from "../lib/configMgr";

// Rolling window: fire a flicker_suspected event when boundary resets >2 times within this ms window
const FLICKER_WINDOW_MS = 1000;
const FLICKER_THRESHOLD = 2;

export const SafeRenderer = ({errorMessage = null, className=null, onError=null, resetKeys=undefined, children = null}) => {
  const conversationContext = useConversationContext();
  const [hasError, setHasError] = useState(false);
  const lastGoodRenderRef = useRef(null);
  const resetTimestampsRef = useRef([]);

  // Capture successful renders
  useEffect(() => {
    if (!hasError && children) {
      lastGoodRenderRef.current = children;
    }
  }, [children, hasError]);

  const fallbackRender = ({error, resetErrorBoundary}) => {
    // Show the last good render with an error indicator overlay
    // This keeps content visible instead of replacing it with an error message
    return (
      <div
        title={`Render error: ${error?.message || error}`}
        className={`safe-renderer-with-error${className ? ' ' + className : ''}`}
        onClick={() => {
          console.log("Attempting to re-render after error");
          setHasError(false);
          resetErrorBoundary();
        }}
      >
        {lastGoodRenderRef.current}
        <span className="render-error-indicator" title="Rendering error occurred - click to retry">...</span>
      </div>
    );
  };

  const handleReset = () => {
    const now = Date.now();
    const timestamps = resetTimestampsRef.current;
    // Trim entries outside the rolling window
    const windowStart = now - FLICKER_WINDOW_MS;
    const recent = timestamps.filter(t => t >= windowStart);
    recent.push(now);
    resetTimestampsRef.current = recent;

    if (recent.length > FLICKER_THRESHOLD) {
      captureMessage(`Flicker suspected: SafeRenderer reset ${recent.length}x in ${FLICKER_WINDOW_MS}ms`, {
        level: "warning",
        tags: {
          error_type: "flicker_suspected",
          in_chat_session: conversationContext?.turns?.length > 0 ? "yes" : "no",
        },
        context: {
          flicker_detection: {
            reset_count: recent.length,
            window_ms: FLICKER_WINDOW_MS,
          },
        },
      });
    }
  };

  const handleError = (error, errorInfo) => {
    // Mark that we have an error so fallback can show last good render
    setHasError(true);

    // Don't use console.error here - it triggers Sentry's console integration
    // and creates duplicate events in addition to our explicit captureMessage
    // Only log to console in development for debugging
    if (DEV) {
      console.error("Render error", error, errorInfo);
    }

    // Prepare context for Sentry
    const sentryContext = {
      error_boundary: {
        component_stack: errorInfo?.componentStack || "N/A",
        error_message: error?.message || String(error),
      },
    };

    // Optionally attach conversation context if available (only when used within ChatSession)
    const attachments = [];
    if (conversationContext?.turns?.length > 0) {
      const conversationData = {
        turns: toJS(conversationContext.turns).map(turn => cleanTurn(turn, true)),
        searchParams: conversationContext.searchParams || null,
        editedTemplates: conversationContext.editedTemplates || {},
      };

      attachments.push({
        filename: "conversation.json",
        data: JSON.stringify(conversationData, null, 2),
        contentType: "application/json",
      });

      sentryContext.conversation_info = {
        turn_count: conversationContext.turns.length,
        has_search_params: !!conversationContext.searchParams,
        has_edited_templates: Object.keys(conversationContext.editedTemplates || {}).length > 0,
      };
    }

    // Capture to Sentry (with conversation context only if available)
    Promise.resolve(captureMessage(`Render error: ${error?.message || String(error)}`, {
      level: "error",
      asException: true,
      errorName: "RenderError",
      tags: {
        error_type: "render_error",
        in_chat_session: attachments.length > 0 ? "yes" : "no",
      },
      context: sentryContext,
      attachments,
    })).catch(sentryError => {
      console.error("Failed to capture render error to Sentry", sentryError);
    });
  };

  return (
    <ErrorBoundary
      className="safe-renderer"
      fallbackRender={fallbackRender}
      onError={onError || handleError}
      onReset={handleReset}
      resetKeys={resetKeys}
    >
      {children}
    </ErrorBoundary>
  );
};


